import { Alert, Box, Button, Tab, Tabs } from '@mui/material';
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import type { RegistryApprovalStatus } from '../../api/registryApiTypes';
import { hasRegistryPermission, type RegistrySession } from '../../session/RegistrySession';
import { useToast } from '../../ui/feedback';
import { PageHeader } from '../../ui/primitives';
import ApprovalList from './ApprovalList';
import DecisionDialog, { type PendingDecision } from './DecisionDialog';
import TestRequestForm from './TestRequestForm';
import { useRegistryApprovals } from './useRegistryApprovals';

const FILTERS: { status: RegistryApprovalStatus; label: string }[] = [
  { status: 'PENDING', label: 'Pending' },
  { status: 'APPROVED', label: 'Approved' },
  { status: 'REJECTED', label: 'Rejected' },
];

/** The live approvals inbox, read from and decided through the registry API. */
export default function ApprovalsInbox({ session }: { session: RegistrySession }) {
  const toast = useToast();
  const [status, setStatus] = useState<RegistryApprovalStatus>('PENDING');
  const [deciding, setDeciding] = useState<PendingDecision | null>(null);
  const { approvals, loading, error, clearError, decide, raiseTest } = useRegistryApprovals(
    session.api,
    status,
  );
  const canRaiseTest =
    session.me.testApprovalsEnabled && hasRegistryPermission(session, 'RegistrySetup');

  const raiseTestRequest = async (title: string, amount?: string) => {
    const ok = await raiseTest(title, amount);
    if (ok) {
      toast('Test request raised');
      setStatus('PENDING');
    }
    return ok;
  };

  const confirmDecision = async ({ item, approve }: PendingDecision, comment?: string) => {
    setDeciding(null);
    const ok = await decide(item.externalId, approve ? 'APPROVE' : 'REJECT', comment);
    if (ok) toast(approve ? 'Approved' : 'Rejected', approve ? 'success' : 'warning');
  };

  return (
    <>
      <PageHeader
        title="Approvals"
        subtitle="Publishing, unit issues and reversals need a second person with Registry Approval. The person who raised a request can never decide it."
        actions={
          <Button component={RouterLink} to="/manager/approvals/prototype" size="small">
            Old prototype approvals (sample data)
          </Button>
        }
      />
      {error && (
        <Alert severity="error" onClose={clearError} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {canRaiseTest && <TestRequestForm onRaise={raiseTestRequest} />}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={status} onChange={(_event, next) => setStatus(next)} aria-label="Status">
          {FILTERS.map((filter) => (
            <Tab key={filter.status} value={filter.status} label={filter.label} />
          ))}
        </Tabs>
      </Box>
      <ApprovalList
        approvals={approvals}
        loading={loading}
        status={status}
        viewerId={session.me.user.externalId}
        viewerCanApprove={hasRegistryPermission(session, 'RegistryApproval')}
        onDecide={(item, approve) => setDeciding({ item, approve })}
      />
      <DecisionDialog
        decision={deciding}
        onClose={() => setDeciding(null)}
        onConfirm={confirmDecision}
      />
    </>
  );
}
